import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { Loader2, Download, FileText, TrendingUp, Wallet } from 'lucide-react';

const fmtKr = (v) => `${Number(v || 0).toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr`;

const WorkerPayslips = () => {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.listMyPayslips();
        setSlips(r.data || []);
      } catch (e) {
        toast.error('Kunde inte ladda lönespecar');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const downloadPdf = async (id, period) => {
    setDownloading(id);
    try {
      const r = await api.downloadMyPayslip(id);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lonespec-${period}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 100);
      toast.success('Lönespec nedladdad');
    } catch (e) {
      toast.error('Kunde inte ladda ner PDF');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-[#E8613A]" />
      </div>
    );
  }

  const totalYear = slips.reduce((s, p) => s + (p.payslip_data?.net_salary || 0), 0);

  return (
    <div className="max-w-3xl mx-auto p-4 pb-24" data-testid="worker-payslips-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Mina lönespecar
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Här hittar du alla dina lönespecifikationer.
        </p>
      </div>

      {slips.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center">
          <Wallet size={32} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-lg font-semibold">Inga lönespecar ännu</p>
          <p className="text-sm text-muted-foreground mt-1">
            När din arbetsgivare kör lönen dyker den upp här. Du får också ett mejl.
          </p>
        </div>
      ) : (
        <>
          {/* Sammanfattning */}
          <div className="bg-gradient-to-br from-[#E8613A] to-[#D14F2A] text-white rounded-2xl p-5 mb-4" data-testid="year-summary">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest opacity-80">Totalt i år (netto)</p>
                <p className="text-3xl font-bold mt-1">{fmtKr(totalYear)}</p>
                <p className="text-xs opacity-80 mt-1">{slips.length} lönespecar</p>
              </div>
              <TrendingUp size={40} className="opacity-30" />
            </div>
          </div>

          {/* Lista */}
          <div className="space-y-3">
            {slips.map(slip => {
              const pd = slip.payslip_data || {};
              const isOpen = expanded === slip.id;
              return (
                <div
                  key={slip.id}
                  className="bg-card border border-border rounded-2xl overflow-hidden"
                  data-testid={`payslip-${slip.id}`}
                >
                  <button
                    onClick={() => setExpanded(isOpen ? null : slip.id)}
                    className="w-full p-4 flex items-center justify-between text-left hover:bg-muted/30 transition-colors"
                  >
                    <div>
                      <p className="font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                        {slip.period_label}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Netto {fmtKr(pd.net_salary)} • Brutto {fmtKr(pd.gross_salary)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#E8613A]">{fmtKr(pd.net_salary)}</p>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-border p-4 bg-muted/10 space-y-3 text-sm">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[10px] uppercase text-muted-foreground">Timmar</p>
                          <p className="font-semibold">
                            {pd.regular_hours || 0} h
                            {pd.overtime_hours > 0 && ` + ${pd.overtime_hours} övt`}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase text-muted-foreground">Bruttolön</p>
                          <p className="font-semibold">{fmtKr(pd.gross_salary)}</p>
                        </div>
                        {pd.vacation_pay > 0 && (
                          <div>
                            <p className="text-[10px] uppercase text-muted-foreground">Semester (12%)</p>
                            <p className="font-semibold">{fmtKr(pd.vacation_pay)}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-[10px] uppercase text-muted-foreground">Preliminärskatt</p>
                          <p className="font-semibold text-red-600">-{fmtKr(pd.income_tax)}</p>
                          {pd.municipal_rate_pct && (
                            <p className="text-[10px] text-muted-foreground">
                              Kommun {pd.municipal_rate_pct.toFixed(2)}%
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="border-t border-border pt-3 flex items-center justify-between">
                        <div>
                          <p className="text-[10px] uppercase text-muted-foreground">Nettolön till konto</p>
                          <p className="text-xl font-bold text-[#E8613A]">{fmtKr(pd.net_salary)}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); downloadPdf(slip.id, slip.period_end); }}
                          disabled={downloading === slip.id}
                          className="flex items-center gap-1.5 px-4 py-2 bg-[#1A1A1A] text-white rounded-full text-sm font-medium hover:bg-[#333] transition-colors"
                          data-testid={`download-pdf-${slip.id}`}
                        >
                          {downloading === slip.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <><Download size={14} /> Ladda ner PDF</>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default WorkerPayslips;
